import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider } from '@arco-design/web-react';
import { useTranslation } from 'react-i18next';
import { getArcoLocale } from './locales/arcoLocale';
import { AuthProvider, useAuth } from './modules/auth/useAuth.jsx';
import ProtectedRoute from './modules/auth/ProtectedRoute';
import AppLayout from './layouts/AppLayout';
import Login from './modules/auth/Login';
import CorporateLogin from './modules/auth/CorporateLogin';
import ErrorBoundary from './modules/core/error-handling/ErrorBoundary.jsx';
import setupCompleteMonitoring from './modules/core/monitoring/setupMonitoring';
import { UnderConstruction } from './modules/common/components/feedback';

// Public page components (eager - needed immediately)
import CatalogPrograms from './modules/public/CatalogPrograms';
import InvitePage from './modules/public/registration/InvitePage';
import RegisterPage from './modules/public/registration/RegisterPage';
import OnboardingRoleRouter from './modules/onboarding/OnboardingRoleRouter';
import OnboardingStatusPage from './modules/onboarding/OnboardingStatusPage';
import OnboardingPublicPage from './modules/onboarding/OnboardingPublicPage';
import PreRegistrationPage from './modules/public/pre-registration/PreRegistrationPage';

// Checkout public pages (post-payment redirects from Stripe)
const CheckoutSuccessPage = React.lazy(() => import('./modules/public/checkout/CheckoutSuccessPage'));
const CheckoutCancelPage = React.lazy(() => import('./modules/public/checkout/CheckoutCancelPage'));

// Lazy-loaded Home
const Home = React.lazy(() => import('./pages/Home'));

// Route groups (each file lazy-loads its own page components)
import { academicRoutes } from './routes/academicRoutes';
import { coordinationRoutes } from './routes/coordinationRoutes';
import { schoolServicesRoutes } from './routes/schoolServicesRoutes';
import { marketingRoutes } from './routes/marketingRoutes';
import { ventasRoutes } from './routes/ventasRoutes';
import { comercialRoutes } from './routes/comercialRoutes';
import { studentRoutes } from './routes/studentRoutes';
import { teacherRoutes } from './routes/teacherRoutes';
import { corporationRoutes } from './routes/corporationRoutes';
import { financeRoutes } from './routes/financeRoutes';
import { hrRoutes } from './routes/hrRoutes';
import { configRoutes } from './routes/configRoutes';
import { maintenanceRoutes } from './routes/maintenanceRoutes';
import { knowledgeRoutes } from './routes/knowledgeRoutes';
import { redirectRoutes } from './routes/redirectRoutes';

import './App.css';

// Wrapper para AppLayout que fuerza re-mount cuando cambia el usuario
const AppLayoutWrapper = () => {
  const { user } = useAuth();
  return <AppLayout key={user?.id || 'no-user'} />;
};

// Configuración de React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    }
  }
});

// Main App content with routes
function AppContent() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/login/corporativo" element={<CorporateLogin />} />
      <Route path="/register" element={<Navigate to="/onboarding" replace />} />
      <Route path="/catalogo" element={<CatalogPrograms />} />

      {/* Public Registration */}
      <Route path="/public/invite/:token" element={<InvitePage />} />
      <Route path="/public/register" element={<RegisterPage />} />
      <Route path="/preinscripcion/:token" element={<PreRegistrationPage />} />

      {/* Checkout post-payment redirects (public, no auth) */}
      <Route path="/pagos/exito" element={
        <React.Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Cargando...</div>}>
          <CheckoutSuccessPage />
        </React.Suspense>
      } />
      <Route path="/pagos/cancelado" element={
        <React.Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Cargando...</div>}>
          <CheckoutCancelPage />
        </React.Suspense>
      } />

      {/* Onboarding */}
      <Route path="/onboarding" element={<OnboardingPublicPage />} />
      <Route path="/onboarding/wizard" element={
        <ProtectedRoute requireOnboardingComplete={false}>
          <OnboardingRoleRouter />
        </ProtectedRoute>
      } />
      <Route path="/onboarding/status" element={
        <ProtectedRoute requireOnboardingComplete={false}>
          <OnboardingStatusPage />
        </ProtectedRoute>
      } />

      {/* Protected routes with AppLayout */}
      <Route element={
        <ProtectedRoute>
          <AppLayoutWrapper />
        </ProtectedRoute>
      }>
        <Route path="/" element={<Home />} />

        {/* Module route groups */}
        {academicRoutes}
        {coordinationRoutes}
        {schoolServicesRoutes}
        {marketingRoutes}
        {ventasRoutes}
        {comercialRoutes}
        {studentRoutes}
        {teacherRoutes}
        {corporationRoutes}
        {financeRoutes}
        {hrRoutes}
        {configRoutes}
        {maintenanceRoutes}
        {knowledgeRoutes}
      </Route>

      {/* Legacy redirects */}
      {redirectRoutes}

      {/* Catch-all */}
      <Route path="*" element={<UnderConstruction />} />
    </Routes>
  );
}

function App() {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (import.meta.env.DEV) {
      setupCompleteMonitoring();
    }
  }, []);

  return (
    <ConfigProvider
      locale={getArcoLocale(i18n.language)}
      componentConfig={{
        Modal: {
          maskClosable: false,
        },
        DatePicker: {
          format: i18n.language?.startsWith('en') ? 'YYYY/MM/DD' : 'DD/MM/YYYY',
        },
      }}
    >
      <ErrorBoundary level="app">
        <QueryClientProvider client={queryClient}>
          <Router future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </Router>
          {import.meta.env.DEV && (
            <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          )}
        </QueryClientProvider>
      </ErrorBoundary>
    </ConfigProvider>
  );
}

export default App;
